import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * A dropdown for secondary toolbar actions.
 *
 * The user-management toolbars had grown to five side-by-side buttons, which
 * filled the card and gave the primary action no visual weight. The primary
 * action stays a button; everything else collapses in here.
 *
 * @param label   {string} trigger text
 * @param icon    {node}   optional icon shown in the trigger
 * @param items   {Array<{key, label, icon, onClick, tone, disabled, description}>}
 * @param align   {"left"|"right"} which edge the panel aligns to
 */
export default function ActionMenu({ label = "Actions", icon = null, items = [], align = "right" }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
        // Return focus to the trigger so keyboard users are not stranded.
        triggerRef.current?.focus();
      }
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, close]);

  const visible = items.filter(Boolean);
  if (!visible.length) return null;

  const tones = {
    default: "text-gray-700",
    primary: "text-[#D67C2C]",
    success: "text-emerald-700",
    info: "text-blue-700",
    violet: "text-violet-700",
    danger: "text-red-600",
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
      >
        {icon}
        {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="menu"
          className={`absolute z-30 mt-2 w-72 rounded-xl border border-gray-200 bg-white py-1.5 shadow-xl ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          {visible.map((item) => (
            <button
              key={item.key || item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              onClick={() => {
                close();
                item.onClick?.();
              }}
              className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className={`mt-0.5 flex-shrink-0 ${tones[item.tone] || tones.default}`}>{item.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-900">{item.label}</span>
                {item.description && (
                  <span className="mt-0.5 block text-xs leading-4 text-gray-500">{item.description}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
